import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button, CircularProgress, Typography } from "@mui/material";
import { FileUpload } from "@outpave/ds";
import type { FileUploadSurface } from "@outpave/ds";
import { Stack, Inline, Columns } from "components/layout/reusableLayouts";

/** Logos are SVG only; the extension covers a browser that labels one some other way. */
export const LOGO_ACCEPT = ".svg,image/svg+xml";

export interface BrandLogoSlot {
  key: string;
  label: string;
  helper: string;
  accept: string;
  /** The package's fixed preview ground: a light logo is judged on light whatever the app's scheme. */
  surface?: FileUploadSurface;
  height?: number;
  /**
   * What this slot shows when nothing is uploaded and nothing is inherited.
   * The node sibling of `fallbackUrls`, for a slot whose empty state is a
   * component rather than an image: the company icon resolves to initials.
   */
  fallback?: React.ReactNode;
}

interface Props {
  slots: BrandLogoSlot[];
  currentUrls: Record<string, string | undefined>;
  fallbackUrls: Record<string, string | undefined>;
  /**
   * Every slot with an operation in flight. A single key cannot describe two
   * at once: starting B unlocked A while A's request was still running, and
   * whichever finished first cleared the other's spinner.
   */
  uploadingKeys: string[];
  onUpload: (key: string, file: File) => Promise<void> | void;
  onRemove: (key: string) => Promise<void> | void;
}

const BrandLogosPanel: React.FC<Props> = ({
  slots,
  currentUrls,
  fallbackUrls,
  uploadingKeys,
  onUpload,
  onRemove,
}) => {
  const { t } = useTranslation();
  /**
   * Claimed synchronously, because `uploadingKeys` is a prop and two calls in
   * one tick both read the pre-commit array. Both would start the same slot,
   * the parent would hold the key twice, and the first to finish would filter
   * every copy out and unlock the slot under the other.
   */
  const claimed = useRef<Set<string>>(new Set());

  /** The one way in, shared by the dropzone and the Replace picker. */
  const startUpload = (key: string, file?: File) => {
    if (!file || uploadingKeys.includes(key) || claimed.current.has(key))
      return;
    claimed.current.add(key);
    Promise.resolve(onUpload(key, file)).finally(() =>
      claimed.current.delete(key),
    );
  };

  return (
    <Stack gap="var(--space-4)">
      <Stack gap="var(--space-1)">
        <Typography variant="h6">{t("Logos")}</Typography>
        <Typography
          variant="body2"
          sx={{ color: "var(--color-text-secondary)" }}
        >
          {t("BrandLogosHelp")}
        </Typography>
      </Stack>

      <Columns count={{ xs: 1, sm: slots.length }} gap="var(--space-4)">
        {slots.map((slot) => {
          const currentUrl = currentUrls[slot.key];
          const previewUrl = currentUrl || fallbackUrls[slot.key];
          const isFallback = !currentUrl && !!previewUrl;
          const isUploading = uploadingKeys.includes(slot.key);
          const height = slot.height ?? 128;

          // Nothing to show yet, so the slot is intake rather than a preview.
          // A slot with its own `fallback` always has something to show.
          const isEmpty =
            !isUploading && !previewUrl && slot.fallback === undefined;

          return (
            <Stack
              key={slot.key}
              gap="var(--space-3)"
              data-testid={`logo-slot-${slot.key}`}
            >
              <Typography variant="subtitle2">{t(slot.label)}</Typography>
              {isEmpty ? (
                <FileUpload
                  variant="dropzone"
                  accept={slot.accept}
                  hint={t(slot.helper)}
                  height={height}
                  onFiles={(files) => startUpload(slot.key, files[0])}
                />
              ) : (
                <>
                  <FileUpload
                    variant="preview"
                    surface={slot.surface ?? "default"}
                    height={height}
                    preview={
                      isUploading ? (
                        <CircularProgress size={20} />
                      ) : previewUrl ? (
                        <img
                          src={previewUrl}
                          alt={t(slot.label)}
                          style={{
                            maxHeight: "55%",
                            maxWidth: "75%",
                            objectFit: "contain",
                            opacity: isFallback ? 0.6 : 1,
                          }}
                        />
                      ) : (
                        slot.fallback
                      )
                    }
                  />
                  <Inline gap="var(--space-2)">
                    <FileUpload
                      variant="trigger"
                      accept={slot.accept}
                      disabled={isUploading}
                      onFiles={(files) => startUpload(slot.key, files[0])}
                    >
                      {(openPicker) => (
                        <Button
                          size="small"
                          variant="default-outlined"
                          disabled={isUploading}
                          onClick={openPicker}
                          aria-label={t("ReplaceLogo", {
                            label: t(slot.label),
                          })}
                        >
                          {t("Replace")}
                        </Button>
                      )}
                    </FileUpload>
                    {currentUrl && (
                      <Button
                        size="small"
                        variant="danger-outlined"
                        onClick={() => onRemove(slot.key)}
                        disabled={isUploading}
                        aria-label={t("RemoveLogo", { label: t(slot.label) })}
                      >
                        {t("Remove")}
                      </Button>
                    )}
                  </Inline>
                </>
              )}
            </Stack>
          );
        })}
      </Columns>
    </Stack>
  );
};

export default BrandLogosPanel;
